/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React from 'react';
import { BrandId } from '../../../../utils/override';
import { mapOfferToPeriodGifts } from '../helpers';
import {
  LoanSearchParams,
  getLoanSearchParamsWithDefaultComponentParam,
} from '../helpers/loanSearchParams';
import { useLoanSearchParams } from '../helpers/useLoanSearchParams';
import { TRANSLATIONS } from '../translations';
import { VerticalType } from '../types';
import { getColorSchema } from './ComponentCredtiCardPromotion/helpers/getColorSchema';
import { getFirstOrSecond } from './ComponentCredtiCardPromotion/helpers/getFirstOrSecond';
import { ProductCampaignGifts } from './ComponentCredtiCardPromotion/ProductCampaignGifts';
import { PromotionCardsHowTo } from './ComponentCredtiCardPromotion/ProductCampaignHowTo';
import { PromotionParticipatingCards } from './ComponentCredtiCardPromotion/ProductCampaignParticipatingCards';
import PromotionCampaingsCarousel from './ComponentCredtiCardPromotion/ProductCampaingsCarousel';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import {
  ComponentPromotionCampaings,
  PromotionCampaign,
  ComponentComparisonTable,
} from '$/(cms)/_modules/cmsService/types';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

const DynamicCampaignBackground = dynamic(
  () =>
    import('./ComponentCredtiCardPromotion/ProductCampaignBackground').then(
      (m) => m.ProductsCampaignBackground,
    ),
  { ssr: false },
);

type Props = ComponentPromotionCampaings & {
  vertical?: VerticalType;
  blockIndex?: number;
  tableConfig?: ComponentComparisonTable;
  brand: BrandId;
  slug?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getActivePromotion = (
  promotionCampaings: PromotionCampaign[],
  activePromotionIndex: number,
) => {
  if (!promotionCampaings?.length) {
    return null;
  }
  return promotionCampaings[activePromotionIndex];
};

const getWelcomeTitle = (
  promotionCampaings: PromotionCampaign[],
  translations: any,
) => {
  if (promotionCampaings.length === 1) {
    return translations.promotion_campaing_welcome_single;
  }

  return translations.promotion_campaing_welcome;
};

const getNextIndex = (campaigns: PromotionCampaign[], currentIndex: number) => {
  if (campaigns.length === 1) return currentIndex;
  return currentIndex === campaigns.length - 1 ? 0 : currentIndex + 1;
};

const CreditCardPromotion = ({
  campaing,
  vertical,
  blockIndex,
  tableConfig,
  brand,
  slug,
}: Props) => {
  const context = useClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const containerRef = React.useRef(null);
  const { loanSearchParams: loanParams } = useLoanSearchParams(
    {} as LoanSearchParams,
  );
  const loanSearchParams = getLoanSearchParamsWithDefaultComponentParam(
    brand,
    loanParams,
    {
      amount: tableConfig?.default_loan_amount
        ? tableConfig?.default_loan_amount.toString()
        : undefined,
      tenure: tableConfig?.default_loan_tenure
        ? tableConfig?.default_loan_tenure.toString()
        : undefined,
    },
  );

  const { setActivePromotionIndex, promotionCampaign, pageType } =
    usePageDataContext();
  const activePromotionIndex = promotionCampaign?.activePromotionIndex ?? 0;

  const promotion_campaing_welcome_single = staticText.welcome_text_single;
  const promotion_campaing_welcome = staticText.welcome_text;

  const promotionCampaigns = campaing?.length ? campaing : [];
  const activePromotion = getActivePromotion(
    promotionCampaigns,
    activePromotionIndex,
  );

  const isSingle = promotionCampaigns.length === 1;

  const isSingleActivePromotionProduct = activePromotion?.products.length === 1;
  const singleActivePromotionProduct = isSingleActivePromotionProduct
    ? (activePromotion?.products?.[0]?.product ?? null)
    : null;

  const welcomeTitle = getWelcomeTitle(promotionCampaigns, {
    promotion_campaing_welcome,
    promotion_campaing_welcome_single,
  });

  const onCampaingPress = (index: number) => {
    setActivePromotionIndex(index);
  };

  const { gradient: gradientBackground } = getColorSchema(
    activePromotion?.color ?? 'Blue',
  );

  React.useEffect(() => {
    const switchTime = (activePromotion?.timer ?? 5) * 1000;
    const timer = setTimeout(() => {
      setActivePromotionIndex(
        getNextIndex(promotionCampaigns, activePromotionIndex),
      );
    }, switchTime);

    return () => clearTimeout(timer);
  }, [activePromotionIndex, promotionCampaigns, isSingle]);

  if (!activePromotion) {
    return null;
  }

  return (
    <div className="relative">
      <DynamicCampaignBackground gradient={gradientBackground} />
      <div
        ref={containerRef}
        id="promotion-campaing-content-block"
        className="relative -mx-4 overflow-x-hidden md:mx-0"
      >
        <div className="relative my-6 md:my-8 md:pl-0 lg:my-12">
          <div className="flex flex-col px-4">
            <h2
              className={classNames(
                'mhg-alpha-typography-bullet-1 mb-2 md:mb-[10px] lg:mb-3',
              )}
            >
              {welcomeTitle}
            </h2>
            <PromotionCampaingsCarousel
              color={activePromotion.color ?? 'blue'}
              campaigns={promotionCampaigns}
              onCampaingPress={onCampaingPress}
              activePromotionIndex={activePromotionIndex}
            />
            <div className="mt-4 flex flex-col-reverse md:mt-6 lg:flex-row lg:justify-between lg:gap-8">
              <div className="flex flex-col gap-4 md:gap-5 lg:max-w-[690px] lg:flex-1 lg:gap-6">
                <div className="flex flex-col gap-spacing/gap/soft-6px">
                  <h4 className="mhg-alpha-typography-display-1 mb-1 text-colors/neutral-gray-900 md:mb-[6px]">
                    {getFirstOrSecond(
                      activePromotion?.title,
                      activePromotion?.exclusive_offer?.offer_title,
                    )}
                  </h4>
                  <p className="mhg-alpha-typography-body-2 lg:mhg-alpha-typography-body-1 text-colors/neutral-gray-900">
                    {getFirstOrSecond(
                      activePromotion?.description,
                      activePromotion?.exclusive_offer?.introduction,
                    )}
                  </p>
                </div>

                <div className="block lg:hidden">
                  <ProductCampaignGifts
                    vertical={vertical}
                    blockIndex={blockIndex}
                    singleProduct={singleActivePromotionProduct}
                    gifts={mapOfferToPeriodGifts([
                      activePromotion!.exclusive_offer!,
                    ])}
                    untilDate={
                      activePromotion?.untilDate ??
                      activePromotion?.exclusive_offer?.period?.end_date
                    }
                  />
                </div>

                <div className="contents flex-col md:flex md:flex-row lg:contents lg:flex-col">
                  <div className="mt-0 w-full md:mt-0 md:w-2/4 lg:mt-6 lg:w-full">
                    <PromotionParticipatingCards
                      activeCampaign={activePromotion}
                      products={activePromotion.products}
                      vertical={vertical}
                      pageType={pageType}
                      loanParams={loanSearchParams}
                      blockIndex={blockIndex}
                      activePromotionIndex={activePromotionIndex}
                      slug={slug}
                    />
                  </div>
                  <div className="w-full md:w-2/4 lg:w-full">
                    <PromotionCardsHowTo
                      text={getFirstOrSecond(
                        activePromotion?.howTo,
                        activePromotion.exclusive_offer?.description,
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="hidden lg:block">
                <ProductCampaignGifts
                  vertical={vertical}
                  gifts={mapOfferToPeriodGifts([
                    activePromotion!.exclusive_offer!,
                  ])}
                  untilDate={getFirstOrSecond(
                    activePromotion?.untilDate,
                    activePromotion?.exclusive_offer?.period?.end_date,
                  )}
                  singleProduct={singleActivePromotionProduct}
                  blockIndex={blockIndex}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCardPromotion;
